/**
 * Mention markup. A comment body stores mentions canonically as
 * `@[Full Name](sales:123)` so the id survives a rename, the display name is
 * re-resolved at render time, and the database only ever trusts the id.
 */

export interface MentionTarget {
  id: number;
  first_name: string;
  last_name: string;
}

export const MENTION_PATTERN = /@\[([^\]]+)\]\(sales:(\d+)\)/g;

export const fullName = (person: {
  first_name?: string | null;
  last_name?: string | null;
}) => `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim();

export const mentionToken = (person: MentionTarget) =>
  `@[${fullName(person)}](sales:${person.id})`;

export type BodySegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; salesId: number; label: string };

/** Split a stored body into plain text and mention segments for rendering. */
export const parseBody = (body: string): BodySegment[] => {
  const segments: BodySegment[] = [];
  let cursor = 0;
  for (const match of body.matchAll(MENTION_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      segments.push({ kind: "text", text: body.slice(cursor, index) });
    }
    segments.push({
      kind: "mention",
      label: match[1],
      salesId: Number(match[2]),
    });
    cursor = index + match[0].length;
  }
  if (cursor < body.length) {
    segments.push({ kind: "text", text: body.slice(cursor) });
  }
  return segments;
};

/**
 * The `@qu` the caret currently sits in, if any, this is what drives the
 * autocomplete. Returns null when the caret is not inside a mention query, so
 * an email address like `a@b.com` never opens the picker.
 */
export const activeMentionQuery = (
  value: string,
  caret: number,
): { query: string; start: number } | null => {
  const before = value.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;
  // Must start a word: preceded by nothing or whitespace.
  if (at > 0 && !/\s/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  // A completed token or a line break ends the query.
  if (/[\s\]()]/.test(query)) return null;
  return { query, start: at };
};

export const applyMention = (
  value: string,
  start: number,
  caret: number,
  person: MentionTarget,
): { text: string; caret: number } => {
  const token = `${mentionToken(person)} `;
  const text = value.slice(0, start) + token + value.slice(caret);
  return { text, caret: start + token.length };
};

export const matchPeople = (people: MentionTarget[], query: string) => {
  const needle = query.toLowerCase();
  return people
    .filter((person) => fullName(person).toLowerCase().includes(needle))
    .slice(0, 6);
};
